import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { InboxGroupDialog } from 'src/features/notifications-v2/components/inbox-group-dialog';
import { graphql, unmaskFragment } from 'src/gql';
import { getAlertContent, getAlertDisplayName, getAlertLink } from 'src/utils/action-center';
import { formatNotificationCreatedAt } from 'src/utils/notification-date-formatting';

import { normalizeOptionalText, NotificationActorDisplay } from './notification-actor-display';

export const NotificationsPeekRowFragment = graphql(/* GraphQL */ `
  fragment NotificationsPeekRow on Alert {
    id
    surfaceAt
    typeOf
    actorKind
    actorName
    actorProfilePicUrl
    actorVerb
    subject
    groupCount
    groupSummary
    groupMembers {
      ...InboxGroupMember
    }
    ...GetAlertDisplayName
    ...GetAlertContent
    ...GetAlertLink
  }
`);

// Mirrors the Inbox tab's category label for a collapsed bulk card (inbox-card.tsx).
const GROUP_CATEGORY_LABEL: Readonly<Record<string, string>> = {
  cash_variance: 'Portfolio & Accounts',
  allocation_variance: 'Portfolio & Accounts',
  rmds: 'Portfolio & Accounts',
  service_calendar_setup: 'Service Calendar',
  service_calendar_due_date: 'Service Calendar',
  service_calendar_task_assigned: 'Service Calendar',
};

type Props = {
  readonly alertFrag: FragmentType<typeof NotificationsPeekRowFragment>;
  /** ISO timestamp of the advisor's last visit. Rows with surfaceAt > visitedAt are unread. */
  readonly visitedAt: string | null;
  readonly onSelect: () => void;
};

function NotificationsPeekRow({ alertFrag, visitedAt, onSelect }: Props) {
  const router = useRouter();
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);

  const alert = unmaskFragment(NotificationsPeekRowFragment, alertFrag);
  const label = getAlertDisplayName(alert);
  const content = getAlertContent(alert) || '';
  const href = getAlertLink(alert);
  const time = formatNotificationCreatedAt({ createdAtIso: alert.surfaceAt });
  const clickable = typeof href === 'string' && href.length > 0;
  const unread = visitedAt === null || alert.surfaceAt > visitedAt;
  const actorName =
    normalizeOptionalText(alert.actorName) ?? (alert.actorKind === 'savvy_cs' ? 'Savvy Client Services' : null);
  const actorVerb = normalizeOptionalText(alert.actorVerb);
  const isActorLed = actorName !== null && actorVerb !== null;
  const primaryText = isActorLed ? `${actorName} ${actorVerb}` : content;
  // ponytail: subject and household.name can coincide (e.g. cash_drift on a
  // single-person household). Drop the duplicate so we don't render
  // "Peggy Custer · Peggy Custer". Keep secondaryText fallback to content
  // when both subject and household are empty.
  const subjectPart = alert.subject !== null && alert.subject !== label ? alert.subject : null;
  const secondaryParts = [subjectPart, label].filter((part) => part !== null && part.length > 0);
  const secondaryText = secondaryParts.length > 0 ? secondaryParts.join(' · ') : content;

  // Collapsed bulk cluster (UNO-588): show the summary + a "View all N" that opens the same
  // members dialog the Inbox tab uses, instead of navigating to a single alert.
  if (alert.groupCount > 1) {
    const categoryLabel = GROUP_CATEGORY_LABEL[alert.typeOf] ?? '';
    return (
      <li>
        <button
          type="button"
          onClick={() => setGroupDialogOpen(true)}
          className="flex w-full items-start gap-2.5 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-[var(--component-hover)]"
        >
          <NotificationActorDisplay
            actorKind={alert.actorKind}
            actorName={alert.actorName}
            actorPhotoUrl={alert.actorProfilePicUrl}
            typeOf={alert.typeOf}
          />
          <span className="sr-only">{unread ? 'Unread' : 'Read'}</span>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-2">
              <Text className={cn('min-w-0 text-sm line-clamp-1', unread ? 'font-semibold' : 'font-medium')}>
                {alert.groupSummary}
              </Text>
              <Text className="shrink-0 text-xs whitespace-nowrap text-secondary">{time}</Text>
            </span>
            <span className="mt-0.5 flex items-center justify-between gap-2">
              <Text className="min-w-0 text-xs text-secondary line-clamp-1">{categoryLabel}</Text>
              <Text className="shrink-0 text-xs font-medium text-primary">{`View all ${alert.groupCount}`}</Text>
            </span>
          </span>
        </button>
        <InboxGroupDialog
          open={groupDialogOpen}
          onOpenChange={setGroupDialogOpen}
          title={alert.groupSummary ?? ''}
          members={alert.groupMembers}
        />
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        disabled={!clickable}
        onClick={() => {
          if (clickable) {
            router.push(href);
            onSelect();
          }
        }}
        className={cn(
          'flex w-full items-start gap-2.5 rounded-md px-3 py-2.5 text-left transition-colors',
          clickable ? 'cursor-pointer hover:bg-[var(--component-hover)]' : 'cursor-default opacity-80',
        )}
      >
        <NotificationActorDisplay
          actorKind={alert.actorKind}
          actorName={alert.actorName}
          actorPhotoUrl={alert.actorProfilePicUrl}
          typeOf={alert.typeOf}
        />
        <span className="sr-only">{unread ? 'Unread' : 'Read'}</span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <Text className={cn('min-w-0 text-sm line-clamp-1', unread ? 'font-semibold' : 'font-medium')}>
              {primaryText}
            </Text>
            <Text className="shrink-0 text-xs whitespace-nowrap text-secondary">{time}</Text>
          </span>
          <Text className="mt-0.5 block text-xs text-secondary line-clamp-1">{secondaryText}</Text>
        </span>
      </button>
    </li>
  );
}

export default NotificationsPeekRow;
